import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, Paper, Snackbar, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { MainApi } from '../../apis/MainApi';

export type CatalogRunOverview = {
    run_id: string;
    tenant_id: string;
    run_ts?: string;
    status?: string;
    current_stage?: string;
    created_at?: string;
    requested_by_email?: string;
};

const TERMINAL_STATUSES = ['Complete', 'Error', 'Aborted'];
const POLL_MS = 15000;
// If the backend's group-cancelled webhook is lost, a run can sit in
// `cancelling` forever with the Cancel button disabled. Expire the badge
// after this many polls (~90s) so the operator can click Cancel again and
// hit the backend's second-click reconcile (cancelledTasks==0 -> local Abort).
const CANCEL_EXPIRY_POLLS = 6;

const isTerminal = (status?: string) => !!status && TERMINAL_STATUSES.includes(status);

// Rejections aren't guaranteed to be Error objects; only a string is renderable.
const errorText = (e: unknown, fallback: string) =>
    e instanceof Error && e.message ? e.message : fallback;

// The backend serializes created_at as a UTC timestamp without an offset
// (naive isoformat), so an offset-less string must be pinned to UTC before
// parsing; rendering is fixed to Israel time regardless of the viewer's zone.
const formatStarted = (run: CatalogRunOverview) => {
    if (!run.created_at) return run.run_ts ?? '—';
    const iso = /(Z|[+-]\d{2}:?\d{2})$/.test(run.created_at) ? run.created_at : `${run.created_at}Z`;
    return new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Jerusalem' });
};

export const RunsTab = ({ mainApi }: { mainApi: MainApi }) => {
    const [runs, setRuns] = useState<CatalogRunOverview[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState<CatalogRunOverview | null>(null);
    // Run ids with a cancel dispatched whose status has not turned terminal yet.
    const [cancelling, setCancelling] = useState<string[]>([]);
    const [feedback, setFeedback] = useState<string | null>(null);
    // Guards every post-await setState below: the Runs tab is conditionally
    // rendered, so a poll tick or an in-flight cancel can resolve after the
    // component has already unmounted (tab switched away).
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);
    // Polls elapsed since each run_id entered `cancelling`, keyed by run_id.
    // Plain ref map — this doesn't drive rendering on its own, only the
    // `cancelling` state (derived from it below) does.
    const cancelPollsRef = useRef<Map<string, number>>(new Map());
    // refresh() runs from both the poll interval and handleCancel, so two can be
    // in flight at once; a slower response must not overwrite a newer one. Each
    // call takes a generation and only the latest may apply its result or error.
    const refreshGenRef = useRef(0);

    // countExpiry: only scheduled poll ticks advance the cancel-expiry counters —
    // the immediate refresh after a cancel POST must not consume one of the
    // CANCEL_EXPIRY_POLLS ticks, or the window shrinks by a poll (~90s -> ~75s).
    const refresh = useCallback(async (countExpiry: boolean) => {
        const gen = ++refreshGenRef.current;
        try {
            const resp = await mainApi.fetchCatalogRunsOverview();
            if (!mountedRef.current || gen !== refreshGenRef.current) return;
            const next = resp.runs ?? [];
            setRuns(next);
            setLoadError(null);
            // A run that reached a terminal status no longer needs its Cancelling badge.
            // One that's been cancelling too long without going terminal (a lost
            // webhook) also drops out, so the Cancel button re-enables.
            setCancelling(prev =>
                prev.filter(id => {
                    if (isTerminal(next.find(r => r.run_id === id)?.status)) {
                        cancelPollsRef.current.delete(id);
                        return false;
                    }
                    if (!countExpiry) return true;
                    const polls = (cancelPollsRef.current.get(id) ?? 0) + 1;
                    if (polls >= CANCEL_EXPIRY_POLLS) {
                        cancelPollsRef.current.delete(id);
                        return false;
                    }
                    cancelPollsRef.current.set(id, polls);
                    return true;
                }));
            // A confirm dialog left open across polls must not offer to cancel
            // a run that has since reached a terminal status.
            setConfirming(prev =>
                prev && isTerminal(next.find(r => r.run_id === prev.run_id)?.status) ? null : prev);
        } catch (e: any) {
            if (!mountedRef.current || gen !== refreshGenRef.current) return;
            setLoadError(errorText(e, 'Failed to load runs'));
        }
    }, [mainApi]);

    useEffect(() => {
        refresh(false);
        const timer = setInterval(() => refresh(true), POLL_MS);
        return () => clearInterval(timer);
    }, [refresh]);

    const handleCancel = async (run: CatalogRunOverview) => {
        setConfirming(null);
        cancelPollsRef.current.set(run.run_id, 0);
        setCancelling(prev => [...prev, run.run_id]);
        try {
            await mainApi.cancelCatalogRun(run.run_id);
            if (!mountedRef.current) return;
            await refresh(false);
        } catch (e: any) {
            cancelPollsRef.current.delete(run.run_id);
            if (!mountedRef.current) return;
            setCancelling(prev => prev.filter(id => id !== run.run_id));
            setFeedback(errorText(e, 'Cancel failed.'));
        }
    };

    if (runs === null && loadError === null) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Paper elevation={1} sx={{ padding: 3 }}>
            {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Tenant</TableCell>
                            <TableCell>Started</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Stage</TableCell>
                            <TableCell>Requested by</TableCell>
                            <TableCell align="right">
                                <Box component="span" sx={{
                                    position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
                                    overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
                                }}>
                                    Actions
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(runs ?? []).map(run => {
                            const pendingCancel = cancelling.includes(run.run_id) && !isTerminal(run.status);
                            return (
                                <TableRow key={run.run_id}>
                                    <TableCell>{run.tenant_id}</TableCell>
                                    <TableCell>{formatStarted(run)}</TableCell>
                                    <TableCell>
                                        {pendingCancel
                                            ? <Chip size="small" label="Cancelling…" color="warning" />
                                            : <Chip size="small" label={run.status ?? 'Unknown'}
                                                color={run.status === 'Aborted' || run.status === 'Error' ? 'error'
                                                    : run.status === 'Complete' ? 'success' : 'info'} />}
                                    </TableCell>
                                    <TableCell>{run.current_stage ?? '—'}</TableCell>
                                    <TableCell>{run.requested_by_email ?? '—'}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            color="error"
                                            disabled={isTerminal(run.status) || pendingCancel}
                                            onClick={() => setConfirming(run)}
                                        >
                                            Cancel
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {runs !== null && runs.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', p: 1 }}>
                                        No recent runs.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={confirming !== null} onClose={() => setConfirming(null)} aria-labelledby="cancel-run-dialog-title">
                <DialogTitle id="cancel-run-dialog-title">Cancel catalog generation?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirming &&
                            `Cancel the run for ${confirming.tenant_id}, started ${formatStarted(confirming)}? ` +
                            'The worker pod will be stopped and the run marked Aborted.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(null)}>Keep running</Button>
                    <Button color="error" variant="contained"
                        onClick={() => confirming && handleCancel(confirming)}>
                        Cancel run
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={feedback !== null} autoHideDuration={6000} onClose={() => setFeedback(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setFeedback(null)} sx={{ width: '100%' }}>
                    {feedback}
                </Alert>
            </Snackbar>
        </Paper>
    );
};
